import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { notificationService } from '../../services/notification.service';
import { createSafeUrl } from '../../utils/safeUrl';
import './Home.scss';

const Home: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [notificationMessage, setNotificationMessage] = useState<string>('');
  const slides = [0, 1, 2, 3];

  const contactInfo = {
    address: '1820 Miccosukee Commons Drive, Tallahassee, FL 32308',
    phone: '(850) 765-8120',
    fax: '(850) 270-7733',
    email: 'info@ffps.pro',
    hours: {
      weekdays: 'Mon-Fri: 8:00AM - 5:00PM',
      saturday: ''
    }
  };

  const mapEmbedUrl = createSafeUrl(`https://www.google.com/maps?q=${encodeURIComponent(contactInfo.address)}&output=embed`);

  useEffect(() => {
    const subscription = notificationService.notification$.subscribe(
      (message) => setNotificationMessage(message)
    );

    const autoPlayInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => {
      subscription.unsubscribe();
      clearInterval(autoPlayInterval);
    };
  }, [slides.length]);

  // Navigation functions (kept for future use if needed)
  // const nextSlide = useCallback(() => {
  //   setCurrentSlide((prev) => (prev + 1) % slides.length);
  // }, [slides.length]);

  // const previousSlide = useCallback(() => {
  //   setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  // }, [slides.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  const openGoogleMaps = useCallback(() => {
    const address = encodeURIComponent(contactInfo.address);
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${address}`;
    window.open(mapsUrl, '_blank');
  }, []);

  const copyToClipboard = useCallback((text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      notificationService.showAppointmentMessage();
    });
  }, []);

  return (
    <div className={`home-container ${notificationMessage && notificationMessage.trim() !== '' ? 'with-notification' : ''}`}>
      <div className={`carousel-section ${notificationMessage && notificationMessage.trim() !== '' ? 'with-notification' : ''}`}>
        <div className="carousel-container">
          <div className={`carousel-slide ${currentSlide === 0 ? 'active' : ''}`}>
            <div className="slide-background slide-1">
              <img src="/assets/images/carousel/carousel-1.png" alt="Welcome to Florida First Psychiatry" />
            </div>
            <div className="slide-content">
              <h2 className="slide-title">Welcome to Florida First Psychiatry</h2>
              <p className="slide-text">Compassionate, adolescents, evidence-based mental health care for adults.</p>
            </div>
          </div>
          <div className={`carousel-slide ${currentSlide === 1 ? 'active' : ''}`}>
            <div className="slide-background slide-2">
              <img src="/assets/images/carousel/carousel-2.png" alt="Personalized Psychiatric Treatment" />
            </div>
            <div className="slide-content">
              <h2 className="slide-title">Personalized Psychiatric Treatment</h2>
              <p className="slide-text">From evaluation to medication management to therapy, we tailor care to your unique needs.</p>
            </div>
          </div>
          <div className={`carousel-slide ${currentSlide === 2 ? 'active' : ''}`}>
            <div className="slide-background slide-3">
              <img src="/assets/images/carousel/carousel-3.png" alt="Advanced Technology & Therapies" />
            </div>
            <div className="slide-content">
              <h2 className="slide-title">Advanced Technology & Therapies</h2>
              <p className="slide-text">Offering the latest in brain-based treatments, including TMS and telehealth services.</p>
            </div>
          </div>
          <div className={`carousel-slide ${currentSlide === 3 ? 'active' : ''}`}>
            <div className="slide-background slide-4">
              <img src="/assets/images/carousel/carousel-4.png" alt="Your Wellness, Our Priority" />
            </div>
            <div className="slide-content">
              <h2 className="slide-title">Your Wellness, Our Priority</h2>
              <p className="slide-text">We partner with you to build lasting mental wellness — your journey matters.</p>
            </div>
          </div>
        </div>
        
        <div className="carousel-controls">
          <div className="carousel-indicators">
            {slides.map((_, index) => (
              <button
                key={index}
                className={`indicator ${currentSlide === index ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="features-section">
        <h2>Our Services at a Glance</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">🔍</div>
            <h3>Comprehensive Psychiatric Evaluation</h3>
            <p>
              Thorough, evidence-based evaluations designed to understand your mental health concerns from every angle.
              We assess psychiatric symptoms, medical history, lifestyle factors, and emotional stressors to build a 
              complete picture of your well-being. This detailed evaluation forms the foundation of your personalized 
              treatment plan and ensures every recommendation is informed, accurate, and clinically sound.
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">💊</div>
            <h3>Medication Management & Psychotherapy</h3>
            <p>
              Integrated treatment plans combining expert medication management with supportive, therapeutic guidance. 
              We closely monitor your response to medications and adjust as needed to ensure safety, effectiveness, 
              and long-term stability. Combined with therapy-informed techniques, this approach helps manage depression, 
              anxiety, bipolar disorder, PTSD, ADHD, and other conditions with clarity and confidence.
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🧠</div>
            <h3>Transcranial Magnetic Stimulation (TMS) & Innovative Therapies</h3>
            <p>
              Cutting-edge brain-based treatments for patients who have not achieved results with traditional approaches. 
              TMS is a non-invasive, FDA-approved therapy that uses gentle magnetic pulses to stimulate areas of the brain 
              involved in mood regulation. We provide comprehensive evaluation, customized TMS protocols, and consistent 
              monitoring to ensure the best possible outcomes for treatment-resistant depression and other complex conditions.
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">💻</div>
            <h3>Telehealth & Flexible Care Options</h3>
            <p>
              Secure, convenient virtual visits designed to fit your schedule while maintaining the highest standard of 
              psychiatric care. Whether at home, work, or traveling, patients can access medication management, follow-ups, 
              and mental health support through our HIPAA-compliant telehealth platform. In-office visits remain available 
              for those who prefer an in-person experience, ensuring flexibility without compromising care quality.
            </p>
          </div>
        </div>
        <div className="services-cta">
          <Link to="/services" className="view-all-services-btn">Learn More About Our Services</Link>
        </div>
      </div>
      <div className="map-section">
        <h2>Find Us</h2>
        <div className="map-container">
          <div className="map-content">
            <div className="map-info">
              <div className="contact-info">
                <div className="contact-item" onClick={openGoogleMaps}>
                  <i className="icon-location"></i>
                  <div className="contact-text">
                    <h3>Address</h3>
                    <p>{contactInfo.address}</p>
                  </div>
                </div>
                <div className="contact-item" onClick={() => copyToClipboard(contactInfo.phone)}>
                  <i className="icon-phone"></i>
                  <div className="contact-text">
                    <h3>Phone</h3>
                    <p>{contactInfo.phone}</p>
                  </div>
                </div>
                <div className="contact-item" onClick={() => copyToClipboard(contactInfo.fax)}>
                  <i className="icon-fax"></i>
                  <div className="contact-text">
                    <h3>Fax</h3>
                    <p>{contactInfo.fax}</p>
                  </div>
                </div>
                <div className="contact-item" onClick={() => copyToClipboard(contactInfo.email)}>
                  <i className="icon-email"></i>
                  <div className="contact-text">
                    <h3>Email</h3>
                    <p>{contactInfo.email}</p>
                  </div>
                </div>
                <div className="contact-item">
                  <i className="icon-time"></i>
                  <div className="contact-text">
                    <h3>Hours</h3>
                    <p>{contactInfo.hours.weekdays}<br />{contactInfo.hours.saturday}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="map-frame">
              <iframe 
                src={mapEmbedUrl}
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Florida First Psychiatry Location"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;

